import React, { useEffect, useState } from "react";
import { ScrollTrigger } from "../../lib/gsap";
import { pillars, services } from "../../data/services";
import { skillGroups } from "../../data/resume";
import { Mask } from "./Chrome";

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" d="M12 5v14M5 12h14" />
  </svg>
);

const FlipIcon = () => (
  <svg className="flip-hint" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 12a8 8 0 0 1 13.7-5.7L20 8.5M20 4v4.5h-4.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12a8 8 0 0 1-13.7 5.7L4 15.5M4 20v-4.5h4.5" />
  </svg>
);

// One line icon per service, keyed by its number; shown inside the floating orb
const SERVICE_ICONS = {
  "01": ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M15.5 8.5l-2 5-5 2 2-5 5-2z"], // compass
  "02": ["M12 3v3", "M8 6h8a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3z", "M9.5 11v2M14.5 11v2", "M2 11v3M22 11v3"], // bot
  "03": ["M5 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM19 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z", "M7 6h3a4 4 0 0 1 4 4 2 2 0 0 0 2 2h1", "M7 18h3a4 4 0 0 0 4-4"], // workflow
  "04": ["M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M11 18h2"], // phone
  "05": ["M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z", "M3 9h18", "M6 7h.01M8.5 7h.01"], // browser
  "06": ["M12 4c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3z", "M4 7v5c0 1.7 3.6 3 8 3s8-1.3 8-3V7", "M4 12v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5"], // knowledge base
};

// The card is a slab: these layers, stacked between the two faces, are its edge
const SLAB_LAYERS = [-9, -6, -3, 0, 3, 6, 9];

function WhatIDo() {
  const [open, setOpen] = useState(0);
  const [tapped, setTapped] = useState(-1);

  /* Service cards are 3D slabs resting at a slight angle. They show their front
     (heading + one line) and flip to their details only while hovered or
     keyboard-focused; that part is plain CSS.
     Touch screens have no hover, so there a tap flips a card and a second tap
     (or a tap on another card) flips it back. */
  const onCardTap = (i) => () => {
    if (window.matchMedia("(hover: none)").matches) setTapped(tapped === i ? -1 : i);
  };

  /* While the mouse is over a card it also leans in 3D: the side under the
     cursor dips away as if pressed, and a soft light follows the pointer. The
     numbers go to CSS variables; the stylesheet does the rest. */
  const onTilt = (e) => {
    if (e.pointerType !== "mouse" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = (e.clientX - box.left) / box.width; // 0 at the left edge … 1 at the right
    const y = (e.clientY - box.top) / box.height;
    card.style.setProperty("--tilt-y", `${(x - 0.5) * 18}deg`);
    card.style.setProperty("--tilt-x", `${(0.5 - y) * 14}deg`);
    card.style.setProperty("--light-x", `${x * 100}%`);
    card.style.setProperty("--light-y", `${y * 100}%`);
  };
  const onTiltEnd = (e) => {
    ["--tilt-x", "--tilt-y", "--light-x", "--light-y"].forEach((name) => e.currentTarget.style.removeProperty(name));
  };

  // Opening a different pillar changes the section's height, which moves every
  // scroll trigger below it; re-measure once the accordion has finished moving
  useEffect(() => {
    const timer = setTimeout(() => ScrollTrigger.refresh(), 650);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <section id="services" className="section">
      <div className="wrap">
        <div className="what">
          <h2 className="section-title">
            <Mask>What</Mask>
            <Mask>
              <span className="outline">I do</span>
            </Mask>
          </h2>

          <div className="what-boxes">
            {pillars.map((p, i) => {
              const isOpen = open === i;
              const tags = skillGroups.find((g) => g.title === p.skillGroup)?.items ?? [];
              return (
                <div
                  key={p.title}
                  className={`what-box ${isOpen ? "what-box--open" : ""}`}
                  onMouseEnter={() => setOpen(i)}
                  data-reveal
                >
                  <button
                    type="button"
                    className="what-box-head"
                    style={{ width: "100%", textAlign: "left" }}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <small>0{i + 1}</small>
                    <h3>{p.title}</h3>
                    <span className="what-box-toggle">
                      <PlusIcon />
                    </span>
                  </button>
                  <div className="what-box-body">
                    <div>
                      <p>{p.description}</p>
                      <h4>Skillset &amp; tools</h4>
                      <div className="tags">
                        {tags.map((t) => (
                          <span key={t} className="tag">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="services-list">
          {services.map((s, i) => (
            <article
              key={s.number}
              className={`flip ${tapped === i ? "flip--tapped" : ""}`}
              tabIndex={0}
              onClick={onCardTap(i)}
              onPointerMove={onTilt}
              onPointerLeave={onTiltEnd}
              data-cursor="dot"
              data-reveal
            >
              <div className="flip-tilt">
                <div className="flip-inner">
                  {SLAB_LAYERS.map((z) => (
                    <span key={z} className="flip-edge" style={{ transform: `translateZ(${z}px)` }} aria-hidden="true" />
                  ))}
                  <div className="flip-face flip-front">
                    <div className="flip-top">
                      <span>{s.number}</span>
                      <FlipIcon />
                    </div>
                    <div className="flip-orb" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        {SERVICE_ICONS[s.number].map((d) => (
                          <path key={d} strokeLinecap="round" strokeLinejoin="round" d={d} />
                        ))}
                      </svg>
                    </div>
                    <h3>{s.title}</h3>
                    <p>{s.tagline}</p>
                  </div>
                  <div className="flip-face flip-back">
                    <div className="flip-top" aria-hidden="true">
                      <span>
                        {s.number} · {s.title}
                      </span>
                      <FlipIcon />
                    </div>
                    <p>{s.description}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhatIDo;
